import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Grid from '../../atoms/Grid/Grid';
import InputControl from '../../atoms/InputControl/InputControl';
import Api from '../../../services/Api';
import './ModalBase.scss';

const fieldLabel = field => (field.charAt(0).toUpperCase() + field.slice(1)).split('_').join(' ');

const normalInput = (field, value, onValueChanged, errMessage, type) => (
  <InputControl
    key={`modal-input-control-${field}`}
    placeholder={`${fieldLabel(field)}...`}
    label={fieldLabel(field)}
    type={type}
    onValueChanged={val => onValueChanged(val)}
    initialValue={value}
    isErrorShown={errMessage !== undefined}
    errorMessage={errMessage || undefined}
  />
);

const selectInput = (field, key, value, onValueChanged) => {
  const label = fieldLabel(key.replace(/_id$/, ''));
  return (
    <div className="form-group">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        <Form.Select
          key={`modal-select-control-${key}`}
          onChange={ev => onValueChanged(ev.target.value)}
        >
          <option value="">---Select Option---</option>
          {field.metadata.options.map(option => (
            // loose equality: select values arrive as strings, stored values as numbers/booleans
            // eslint-disable-next-line eqeqeq
            <option value={option.value} selected={option.value == value}>{option.label}</option>
          ))}
        </Form.Select>
      </Form.Group>
    </div>
  );
};

// metadata.createEndpoint makes it work like tags: typing a new name creates it there
const MultiSelectInput = ({
  field,
  fieldKey,
  value,
  onValueChanged,
  errMessage,
}) => {
  const [createdOptions, setCreatedOptions] = useState([]);
  const label = fieldLabel(fieldKey.replace(/_ids$/, ''));
  const { createEndpoint } = field.metadata;
  const options = [...field.metadata.options, ...createdOptions];
  const selectProps = {
    options,
    value: options.filter(option => (value || []).includes(option.value)),
    onChange: selected => onValueChanged(selected.map(option => option.value)),
    placeholder: `Select ${label}...`,
    isMulti: true,
  };
  const onCreateOption = (name) => {
    Api.post(createEndpoint, { name }, false).then(({ status, data }) => {
      if (status === 200 || status === 201) {
        setCreatedOptions(previous => [...previous, { label: data.name, value: data.id }]);
        onValueChanged([...(value || []), data.id]);
      }
    });
  };
  return (
    <div className="form-group">
      <Form.Group>
        <Form.Label>{label}</Form.Label>
        {createEndpoint ? (
          <CreatableSelect
            {...selectProps}
            onCreateOption={onCreateOption}
            formatCreateLabel={name => `Add "${name}"`}
          />
        ) : (
          <Select {...selectProps} />
        )}
        {errMessage && <Form.Text className="text-danger">{errMessage}</Form.Text>}
      </Form.Group>
    </div>
  );
};

MultiSelectInput.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  field: PropTypes.object.isRequired,
  fieldKey: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.array,
  onValueChanged: PropTypes.func.isRequired,
  errMessage: PropTypes.string,
};

MultiSelectInput.defaultProps = {
  value: undefined,
  errMessage: undefined,
};

const getInput = (field, key, value, onValueChanged, errMessage) => {
  if (field.metadata === undefined) {
    return normalInput(key, value, onValueChanged, errMessage);
  }
  switch (field.metadata.type) {
    case 'select':
      return selectInput(field, key, value, onValueChanged);
    case 'multiselect':
      return (
        <MultiSelectInput
          key={`modal-multiselect-control-${key}`}
          field={field}
          fieldKey={key}
          value={value}
          onValueChanged={onValueChanged}
          errMessage={errMessage}
        />
      );
    case 'textarea':
      return normalInput(key, value, onValueChanged, errMessage, 'textarea');
    case 'number':
      return normalInput(key, value, onValueChanged, errMessage, 'number');
    default:
      return normalInput(key, value, onValueChanged, errMessage);
  }
};

const ModalBase = ({
  fields,
  endpoint,
  editableItem,
  onRequestClose,
  afterEdit,
  afterCreate,
  extraParameters,
}) => {
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  fields.forEach((item) => {
    const simpleField = typeof item === 'string';
    let initialValue;
    if (editableItem && simpleField) {
      initialValue = editableItem[item];
    } else if (editableItem && !simpleField) {
      // valueFrom derives the value from the record, e.g. scholars -> scholars_ids
      initialValue = item.valueFrom ? item.valueFrom(editableItem) : editableItem[item.key];
    } else if (!simpleField) {
      initialValue = item.defaultValue;
    }
    const [value, setValue] = useState(initialValue);
    const identifier = {
      key: simpleField ? item : item.key,
      value,
      setValue,
      metadata: simpleField ? undefined : {
        type: item.type,
        options: item.options,
        createEndpoint: item.createEndpoint,
      },
    };
    const exists = states.find((x) => {
      if (simpleField) {
        return x.key === item;
      }
      return x.key === item.key;
    });
    if (exists) {
      return;
    }
    setStates(prevState => [
      ...prevState,
      identifier,
    ]);
  });
  const onSave = () => {
    const body = states.reduce((result, item) => {
      if (item.value) {
        // eslint-disable-next-line no-param-reassign
        result[item.key] = item.value;
      }
      return result;
    }, {});
    if (extraParameters) {
      extraParameters.forEach((item) => {
        Object.keys(item).forEach((key) => {
          body[key] = item[key];
        });
      });
    }
    setIsLoading(true);
    if (!editableItem) {
      Api.post(endpoint, body, false).then(({ status, data }) => {
        if (status === 201) {
          if (afterCreate) {
            afterCreate(data);
          }
          if (onRequestClose) {
            onRequestClose();
          }
        } else if (status === 422) {
          const keys = Object.keys(data.errors);
          const fieldsErrors = [];
          keys.forEach(key => fieldsErrors.push({
            key,
            message: data.errors[key][0],
          }));
          setErrors(fieldsErrors);
        }
        setIsLoading(false);
      });
    } else {
      Api.patch(`${endpoint}/${editableItem.id}`, body, false).then(({ status, data }) => {
        if (status === 200) {
          if (afterEdit) {
            afterEdit(data);
          }
          if (onRequestClose) {
            onRequestClose();
          }
        }
        setIsLoading(false);
      });
    }
  };
  return (
    <div className="modal-base">
      <Grid>
        {states.length > 0 && fields && fields.map((item) => {
          const itemKey = typeof item === 'string' ? item : item.key;
          const identifier = states.find(x => x.key === itemKey);
          if (!identifier) {
            return undefined;
          }
          const {
            value,
            setValue,
            key,
          } = identifier;
          const errorMessage = errors.find(x => x.key === itemKey);
          return (
            <div className="col-md-12">
              {getInput(identifier, key, value, (val) => {
                setValue(val);
                const newStates = states.filter(x => x.key !== itemKey);
                setStates([
                  ...newStates,
                  {
                    key: itemKey,
                    value: val,
                    setValue,
                    metadata: identifier.metadata,
                  },
                ]);
              }, errorMessage ? errorMessage.message : undefined)}
            </div>
          );
        })}
        <div className="col-md-12" style={{ marginTop: '10px' }}>
          <Button onClick={onSave} variant="dark" disabled={isLoading}>
            {!isLoading ? (
              <>
                Save
              </>
            ) : (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="visually-hidden">Loading...</span>
              </>
            )}
          </Button>
        </div>
      </Grid>
    </div>
  );
};

ModalBase.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  editableItem: PropTypes.object,
  onRequestClose: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fields: PropTypes.array,
  endpoint: PropTypes.string,
  afterEdit: PropTypes.func,
  afterCreate: PropTypes.func,
  extraParameters: PropTypes.array,
};

ModalBase.defaultProps = {
  editableItem: undefined,
  fields: [],
  endpoint: undefined,
  afterCreate: undefined,
  afterEdit: undefined,
  extraParameters: undefined,
};

export default ModalBase;
